import {
  DEFAULT_SYMBOLIC_PATH,
  STORAGE_URL,
  SystemFolder,
} from 'commons/constants/KeyFlowConstants';
import HostFactory from 'commons/handler/HostFactory';

/**
 * Utils
 */
import {
  ScriptThumbnailRelativeURLBuilder,
  ThumbnailRelativeURLBuilder,
} from 'commons/utils/MediaURLBuilder';
import { formattedDateString, assetWithOutToken, assetWithToken } from 'commons/utils/commonUtil';

/**
 * VO
 */
import KeyFlowVO from 'commons/vo/KeyFlowVO';
import defaultThumbnail from 'asset/thumbnail/thumb_default.jpg';
import { API } from 'commons/constants/KeyFlowAPI';

type WkblScriptVOProps = {
  ID: number;
  CATEGORY_ID: number;
  TYPE: string;
  RANGE: string;
  SCRIPT: string;
  OWNER: number;
  VIDEO_ID: number;
  IN_POINT: number;
  OUT_POINT: number;
  THUMBNAIL_PATH: string;
  THUMBNAIL_FRAME: number;
  DOWNLOAD_COUNT: number;
  RATING: number;
  DATE_OF_CREATED: string;
  DATE_OF_MODIFIED: string;
  TITLE: string;
  TEAM_1: number;
  TEAM_1_NAME: string;
  TEAM_1_LOGO: string;
  TEAM_1_COLOR: string;
  TEAM_2: number;
  TEAM_2_NAME: string;
  TEAM_2_LOGO: string;
  TEAM_2_COLOR: string;
  SEASON: string;
  GAME_TYPE: number;
  GAME_DATE: string;
  MEDIA_FPS: number;
};

class WkblScriptVO extends KeyFlowVO {
  id: number;
  categoryId: number;
  type: string;
  range: string;
  script: string;
  owner: number;
  videoId: number;
  inPoint: number;
  outPoint: number;
  thumbnailPath: string;
  thumbnailFrame: number;
  downloadCount: number;
  rating: number;
  dateOfCreated: string;
  dateOfModified: string;
  title: string;
  team1: number;
  team1Name: string;
  team1Logo: string;
  team1Color: string;
  team2: number;
  team2Name: string;
  team2Logo: string;
  team2Color: string;
  season: string;
  gameType: number;
  gameDate: string;
  mediaFps: number;

  _symbolicPath: string;

  constructor(data: WkblScriptVOProps) {
    super(data);
    this.id = data.ID;
    this.categoryId = data.CATEGORY_ID;
    this.type = data.TYPE;
    this.script = data.SCRIPT;
    this.videoId = data.VIDEO_ID;
    this.inPoint = data.IN_POINT;
    this.outPoint = data.OUT_POINT;
    this.downloadCount = data.DOWNLOAD_COUNT;
    this.rating = data.RATING;
    this.dateOfCreated = data.DATE_OF_CREATED;
    this.dateOfModified = data.DATE_OF_MODIFIED;
    this.title = data.TITLE;
    this.gameDate = data.GAME_DATE;
    this.range = data.RANGE;
    this.season = data.SEASON;
    this.thumbnailPath = data.THUMBNAIL_PATH;
    this.thumbnailFrame = data.THUMBNAIL_FRAME;
    this.owner = data.OWNER;
    this.team1 = data.TEAM_1;
    this.team1Name = data.TEAM_1_NAME;
    this.team1Logo = data.TEAM_1_LOGO;
    this.team1Color = data.TEAM_1_COLOR;
    this.team2 = data.TEAM_2;
    this.team2Name = data.TEAM_2_NAME;
    this.team2Logo = data.TEAM_2_LOGO;
    this.team2Color = data.TEAM_2_COLOR;
    this.gameType = data.GAME_TYPE;
    this.mediaFps = data.MEDIA_FPS;

    this._symbolicPath = '';
  }

  get symbolicPath() {
    return this._symbolicPath || DEFAULT_SYMBOLIC_PATH;
  }
  set symbolicPath(symbolicPath: string) {
    this._symbolicPath = symbolicPath;
  }

  get formattedGameDate() {
    return formattedDateString(this.gameDate)?.split(' ')[0];
  }

  getTeam1LogoPath(symbolicPath: string) {
    const url = `${STORAGE_URL}${symbolicPath}/${SystemFolder.LOGO_NAME}/${this.team1Logo}`;
    return assetWithOutToken(url);
  }

  getTeam2LogoPath(symbolicPath: string) {
    const url = `${STORAGE_URL}${symbolicPath}/${SystemFolder.LOGO_NAME}/${this.team2Logo}`;
    return assetWithOutToken(url);
  }

  getTeamLogoPath() {
    const { symbolicPath } = this;
    return {
      team1: {
        nas: this.getTeam1LogoPath(symbolicPath),
        cloudfront: `${HostFactory.cloudfrontURL}/${SystemFolder.LOGO_NAME}/${this.team1Logo}`,
        db: assetWithOutToken(`${HostFactory.apiURL}${API.TEAM_LOGO}/${this.team1}`),
      },
      team2: {
        nas: this.getTeam2LogoPath(symbolicPath),
        cloudfront: `${HostFactory.cloudfrontURL}/${SystemFolder.LOGO_NAME}/${this.team2Logo}`,
        db: assetWithOutToken(`${HostFactory.apiURL}${API.TEAM_LOGO}/${this.team2}`),
      },
    };
  }

  get thumbnailRelativeURL() {
    return new ScriptThumbnailRelativeURLBuilder()
      .categoryId(this.categoryId)
      .refId(this.videoId)
      .fileName(this.thumbnailPath)
      .build();
  }

  getThumbnailFilePath(symbolicPath: string) {
    const url = `${STORAGE_URL}${symbolicPath}/${this.thumbnailRelativeURL}`;
    return assetWithToken(url);
  }

  getFilePath() {
    const { symbolicPath } = this;
    return {
      thumbnail: {
        nas: this.getThumbnailFilePath(symbolicPath),
        cloudfront: `${HostFactory.cloudfrontURL}/${this.thumbnailRelativeURL}`,
      },
    };
  }

  getBackgroundImage() {
    const { nas, cloudfront } = this.getFilePath().thumbnail;
    return `url(${cloudfront}), url(${nas}), url(${defaultThumbnail})`;
  }
}

export default WkblScriptVO;
